// ============================================================
//  Dropdown preset selector with optional custom date inputs.
//  Presets: This Week · Today · Yesterday · Last 7 Days ·
//           Last Week (Mon–Sun) · Last 14 Days · This Month ·
//           Last 30 Days · Last Month · All Time
// ============================================================
import React, { useState, useCallback, useRef, useEffect } from "react";

import calendarIcon from "../assets/icons/calendar.svg";
import chevDownIcon from "../assets/icons/chevron-down.svg";

// ── Date helpers ─────────────────────────────────────────────
const fmt = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];         // yyyy-MM-dd
};

const todayDate = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const addDays = (date, n) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
};

const startOfWeek = (date) => {           // Monday
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

const endOfWeek = (date) => {             // Sunday
    const d = startOfWeek(date);
    d.setDate(d.getDate() + 6);
    return d;
};

const startOfMonth = (date) => {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
};

const endOfMonth = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
};

// Display format: DD/MM/YYYY
const display = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
};

// ── Presets ──────────────────────────────────────────────────
const buildPresets = () => {
    const t = todayDate();
    const prevMonthDate = new Date(t.getFullYear(), t.getMonth() - 1, 1);

    return [
        {
            key: "thisWeek",
            label: "This Week",
            start: fmt(startOfWeek(t)),
            end: fmt(t),
        },
        {
            key: "today",
            label: "Today",
            start: fmt(t),
            end: fmt(t),
        },
        {
            key: "yesterday",
            label: "Yesterday",
            start: fmt(addDays(t, -1)),
            end: fmt(addDays(t, -1)),
        },
        {
            key: "last7",
            label: "Last 7 Days",
            start: fmt(addDays(t, -6)),
            end: fmt(t),
        },
        {
            key: "lastWeek",
            label: "Last Week (Mon–Sun)",
            start: fmt(startOfWeek(addDays(t, -7))),
            end: fmt(endOfWeek(addDays(t, -7))),
        },
        {
            key: "last14",
            label: "Last 14 Days",
            start: fmt(addDays(t, -13)),
            end: fmt(t),
        },
        {
            key: "thisMonth",
            label: "This Month",
            start: fmt(startOfMonth(t)),
            end: fmt(t),
        },
        {
            key: "last30",
            label: "Last 30 Days",
            start: fmt(addDays(t, -29)),
            end: fmt(t),
        },
        {
            key: "lastMonth",
            label: "Last Month",
            start: fmt(startOfMonth(prevMonthDate)),
            end: fmt(endOfMonth(prevMonthDate)),
        },
        {
            key: "allTime",
            label: "All Time",
            start: "2024-04-01",
            end: fmt(t),
        },
        {
            key: "custom",
            label: "Custom Range",
            start: null,
            end: null,
        },
    ];
};

// ── Component ────────────────────────────────────────────────
/**
 * Props:
 *   onChange(startDate, endDate)  – fires whenever dates change
 *   defaultPreset?                – key string, default "thisWeek"
 */
const DateRangePicker = ({ onChange, defaultPreset = "thisWeek" }) => {
    const presets = buildPresets();
    const defaultP = presets.find(p => p.key === defaultPreset) || presets[0];

    const [activePreset, setActivePreset] = useState(defaultP);
    const [dropOpen, setDropOpen] = useState(false);
    const [customStart, setCustomStart] = useState(fmt(todayDate()));
    const [customEnd, setCustomEnd] = useState(fmt(todayDate()));

    const dropRef = useRef(null);

    // Fire default on mount
    useEffect(() => {
        onChange?.(defaultP.start, defaultP.end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) {
                setDropOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const selectPreset = useCallback((preset) => {
        setActivePreset(preset);
        if (preset.key !== "custom") {
            setDropOpen(false);
            onChange?.(preset.start, preset.end);
        }
    }, [onChange]);

    const handleCustomStart = (e) => {
        setCustomStart(e.target.value);
        onChange?.(e.target.value, customEnd);
    };

    const handleCustomEnd = (e) => {
        setCustomEnd(e.target.value);
        onChange?.(customStart, e.target.value);
    };

    // Label shown in the trigger button
    const triggerLabel = activePreset.key === "custom"
        ? `${display(customStart)} - ${display(customEnd)}`
        : activePreset.label;

    // Date range subtitle shown in trigger
    const rangeSubLabel = activePreset.key === "custom"
        ? null
        : `${display(activePreset.start)} - ${display(activePreset.end)}`;

    return (
        <div style={styles.wrap} ref={dropRef}>

            {/* ── Trigger button ── */}
            <button
                style={styles.trigger}
                onClick={() => setDropOpen(o => !o)}
                type="button"
            >
                <img src={calendarIcon} alt="" width={14} height={14} style={{ flexShrink: 0 }} />
                <div style={styles.triggerText}>
                    <span style={styles.triggerLabel}>{triggerLabel}</span>
                    {rangeSubLabel && (
                        <span style={styles.triggerSub}>{rangeSubLabel}</span>
                    )}
                </div>
                <img
                    src={chevDownIcon}
                    alt=""
                    width={13}
                    height={13}
                    style={{
                        flexShrink: 0,
                        opacity: 0.45,
                        transition: "transform 0.15s ease",
                        transform: dropOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                />
            </button>

            {/* ── Dropdown ── */}
            {dropOpen && (
                <div style={styles.dropdown}>
                    {presets.map((p, idx) => {
                        const isActive = activePreset.key === p.key;
                        const isCustom = p.key === "custom";
                        const isDividerBefore = idx === presets.length - 1; // divider before Custom

                        return (
                            <React.Fragment key={p.key}>
                                {isDividerBefore && <div style={styles.dropDivider} />}
                                <button
                                    style={{
                                        ...styles.dropItem,
                                        ...(isActive ? styles.dropItemActive : {}),
                                    }}
                                    onClick={() => selectPreset(p)}
                                    type="button"
                                >
                                    <div style={styles.dropItemLeft}>
                                        {/* Tick mark for active */}
                                        <span style={styles.tick}>
                                            {isActive ? "✓" : ""}
                                        </span>
                                        <span style={styles.dropItemLabel}>{p.label}</span>
                                    </div>
                                    {!isCustom && (
                                        <span style={styles.dropItemRange}>
                                            {display(p.start)} – {display(p.end)}
                                        </span>
                                    )}
                                </button>

                                {/* Custom date inputs — shown inline when Custom is selected */}
                                {isCustom && isActive && (
                                    <div style={styles.customInputs}>
                                        <div style={styles.customField}>
                                            <label style={styles.customLabel}>Start</label>
                                            <input
                                                type="date"
                                                style={styles.customDate}
                                                value={customStart}
                                                max={customEnd}
                                                onChange={handleCustomStart}
                                                min="2024-04-01"
                                            />
                                        </div>
                                        <span style={styles.customArrow}>→</span>
                                        <div style={styles.customField}>
                                            <label style={styles.customLabel}>End</label>
                                            <input
                                                type="date"
                                                style={styles.customDate}
                                                value={customEnd}
                                                min={customStart}
                                                onChange={handleCustomEnd}
                                            />
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Styles ───────────────────────────────────────────────────
const styles = {
    wrap: {
        position: "relative",
        display: "inline-block",
    },

    /* Trigger */
    trigger: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 14px",
        background: "#fff",
        border: "1px solid #D1D5DB",
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        minWidth: 240,
    },
    triggerText: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 1,
    },
    triggerLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: "#111827",
        lineHeight: 1.3,
    },
    triggerSub: {
        fontSize: 11,
        color: "#9CA3AF",
        fontFamily: "'DM Mono', monospace",
        lineHeight: 1.2,
    },

    /* Dropdown panel */
    dropdown: {
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        zIndex: 200,
        minWidth: 300,
        overflow: "hidden",
        padding: "4px 0",
    },
    dropDivider: {
        height: 1,
        background: "#F3F4F6",
        margin: "4px 0",
    },

    /* Dropdown item */
    dropItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "9px 14px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
        gap: 8,
        transition: "background 0.1s ease",
    },
    dropItemActive: {
        background: "#F0FDF4",
    },
    dropItemLeft: {
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    tick: {
        width: 14,
        fontSize: 12,
        color: "#4CAF50",
        fontWeight: 700,
        flexShrink: 0,
    },
    dropItemLabel: {
        fontSize: 13,
        fontWeight: 500,
        color: "#111827",
    },
    dropItemRange: {
        fontSize: 11,
        color: "#9CA3AF",
        fontFamily: "'DM Mono', monospace",
        whiteSpace: "nowrap",
    },

    /* Custom date inputs */
    customInputs: {
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        padding: "8px 14px 12px 36px",
        background: "#F9FAFB",
        borderTop: "1px solid #F3F4F6",
    },
    customField: {
        display: "flex",
        flexDirection: "column",
        gap: 4,
        flex: 1,
    },
    customLabel: {
        fontSize: 11,
        fontWeight: 500,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    customDate: {
        padding: "6px 10px",
        border: "1px solid #D1D5DB",
        borderRadius: 6,
        fontSize: 13,
        fontFamily: "inherit",
        color: "#111827",
        background: "#fff",
        outline: "none",
        width: "100%",
    },
    customArrow: {
        fontSize: 14,
        color: "#9CA3AF",
        paddingBottom: 6,
        flexShrink: 0,
    },
};

export default DateRangePicker;
